/**
 * Insurer Search Hook
 *
 * Provides insurer search functionality with dialog handling for various scenarios
 * Validates insurance type and insurer number format per specification section 12.4
 */

import { useState, useRef, useCallback } from 'react';
import { useAuth } from '@shared/providers/AuthContext';
import { useInsurerDialogMessages } from '@/hooks/main/useInsurerDialogMessages';
import {
  INSURER_SEARCH_PAGE_SIZE,
  useSearchInsurers,
  type Insurer,
  type InsurerSearchPagination,
  type SearchInsurersResult,
} from '@/hooks/main/useInsurerSearch';
import useAppModal from '@/hooks/useAppModal';
import { fetchInsurerDetail } from '@pages/main/patient/services';
import { validateInsurerNumberPrefix } from '@pages/main/patient/types';

export interface InsurerSearchResult {
  id: number;
  insurer_number: string;
  insurer_kind_name: string;
  insurer_name: string;
  insurance_type_id: number;
  claim_type_id?: number | null;
  has_payment_history: boolean;
  patient_cost_rate?: string;
  applicable_period?: string;
}

interface UseInsurerSearchOptions {
  onSuccess?: (results: InsurerSearchResult[]) => void;
  onError?: (error: Error) => void;
  onClearInput?: () => void;
}

function mapInsurerToListResult(item: Insurer): InsurerSearchResult {
  return {
    id: item.id,
    insurer_number: item.insurer_number ?? '',
    insurer_kind_name: item.insurer_type?.name ?? '',
    insurer_name: item.insurer_name ?? '',
    insurance_type_id: item.insurer_type_id ?? 1,
    claim_type_id: item.medix_claim_type_id ?? null,
    has_payment_history: item.medix_claim_type_id !== 1,
  };
}

function isPaginatedResult(
  r: SearchInsurersResult
): r is { data: Insurer[]; pagination: InsurerSearchPagination } {
  return typeof r === 'object' && r !== null && 'pagination' in r && 'data' in r;
}

export function useInsurerSearch(options?: UseInsurerSearchOptions) {
  const { clinic } = useAuth();
  const {
    getWarningDialog1Message,
    getWarningDialog2Message,
    // getMultipleResultsWarningMessage, // kept for restore: was used for 一般 merger warning
    getEmptyInsurerNumberMessage,
  } = useInsurerDialogMessages();
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<InsurerSearchResult[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentInsuranceTypeId, setCurrentInsuranceTypeId] = useState<number>(1);
  const [insurerSearchTotalCount, setInsurerSearchTotalCount] = useState(0);
  const lastSearchedInsurerNumberRef = useRef<string>('');
  const lastSearchTotalCountRef = useRef<number>(0);
  const onSelectCallbackRef = useRef<((result: InsurerSearchResult) => void) | null>(null);
  const onCloseCallbackRef = useRef<(() => void) | null>(null);
  const onClearInputCallbackRef = useRef<(() => void) | null>(null);
  const modal = useAppModal();

  // Use React Query mutation for insurer search (listOnly: minimal fields for table; on select use GET detail for form)
  const { mutate: searchMutate, fetchPage } = useSearchInsurers(
    clinic?.site_type || 'medix',
    clinic?.clinic_number || '',
    { listOnly: true, healthInsuranceOnly: true }
  );

  const handleSearch = useCallback(
    async (
      insurerNumber: string,
      insuranceTypeId?: number
    ): Promise<InsurerSearchResult[] | null> => {
      // Scenario: No insurer number entered
      if (!insurerNumber || insurerNumber.trim() === '') {
        modal.warning({
          title: '警告',
          content: getEmptyInsurerNumberMessage(),
          okText: 'OK',
        });
        return null;
      }

      // Validate insurer number format based on insurance type
      const validation = validateInsurerNumberPrefix(null, insuranceTypeId || null, insurerNumber);
      if (!validation.isValid) {
        modal.warning({
          title: '警告',
          content: validation.message || '保険者番号の形式が正しくありません。',
          okText: 'OK',
          onOk: () => {
            // Clear input when user closes the error dialog
            onClearInputCallbackRef.current?.();
          },
        });
        return null;
      }

      if (!clinic || !clinic.site_type || !clinic.clinic_number) {
        throw new Error('Clinic information is not available');
      }

      setIsLoading(true);
      setInsurerSearchTotalCount(0);

      return new Promise(resolve => {
        searchMutate(
          { insurerNumber, page: 1, perPage: INSURER_SEARCH_PAGE_SIZE },
          {
            onSuccess: (results: SearchInsurersResult) => {
              setIsLoading(false);

              const typedResults: InsurerSearchResult[] = isPaginatedResult(results)
                ? (results.data ?? []).map(mapInsurerToListResult)
                : ((results as unknown as InsurerSearchResult[]) ?? []);
              const totalCount = isPaginatedResult(results)
                ? (results.pagination.total ?? 0)
                : typedResults.length;
              lastSearchTotalCountRef.current = totalCount;

              // Scenario: Invalid insurer number (no results found)
              if (!typedResults || typedResults.length === 0 || totalCount === 0) {
                const dialogResponse = getWarningDialog2Message();
                modal.warning({
                  title: '警告',
                  content: dialogResponse.message,
                  okText: 'OK',
                  onOk: () => {
                    // Clear input when user closes the error dialog if shouldClearInsurerNumber is true
                    if (dialogResponse.shouldClearInsurerNumber) {
                      options?.onClearInput?.();
                    }
                  },
                });
                resolve(null);
                return;
              }

              // Scenario: Exact match (1 result)
              if (totalCount === 1 && typedResults.length === 1) {
                const result = typedResults[0] as InsurerSearchResult & {
                  cost_rate?: number;
                  has_payment_history?: boolean;
                };

                // Only block when API explicitly says no payment history (missing/undefined = allow)
                if (result.has_payment_history === false) {
                  const dialogResponse = getWarningDialog1Message();
                  modal.warning({
                    title: '警告',
                    content: dialogResponse.message,
                    okText: 'OK',
                    onOk: () => {
                      if (dialogResponse.shouldClearInsurerNumber) {
                        options?.onClearInput?.();
                      }
                    },
                  });
                  resolve(null);
                  return;
                }

                // List response has no cost_rate; fetch detail for form fill
                if (
                  result.cost_rate == null &&
                  result.id != null &&
                  clinic?.site_type &&
                  clinic?.clinic_number
                ) {
                  fetchInsurerDetail(clinic.site_type, clinic.clinic_number, result.id)
                    .then(detail => {
                      if ((detail.medix_claim_type_id ?? null) === 1) {
                        const dialogResponse = getWarningDialog1Message();
                        modal.warning({
                          title: '警告',
                          content: dialogResponse.message,
                          okText: 'OK',
                          onOk: () => {
                            if (dialogResponse.shouldClearInsurerNumber) {
                              options?.onClearInput?.();
                            }
                          },
                        });
                        resolve(null);
                        return;
                      }

                      const fullResult = {
                        id: detail.id,
                        insurer_number: detail.insurer_number ?? '',
                        insurer_name: detail.insurer_name ?? '',
                        insurer_kind_name: detail.insurer_type?.name ?? '一般',
                        insurance_type_id: 1,
                        claim_type_id: detail.medix_claim_type_id ?? null,
                        has_payment_history: detail.medix_claim_type_id !== 1,
                        patient_cost_rate: String(detail.cost_rate ?? '3'),
                        cost_rate: detail.cost_rate ?? 0,
                        family_cost_rate: detail.family_cost_rate ?? 0,
                        insurer_type: detail.insurer_type,
                      } as InsurerSearchResult;
                      setSearchResults([fullResult]);
                      options?.onSuccess?.([fullResult]);
                      resolve([fullResult]);
                    })
                    .catch(() => {
                      setIsLoading(false);
                      resolve(null);
                    });
                  return;
                }

                setSearchResults(typedResults);
                options?.onSuccess?.(typedResults);
                resolve(typedResults);
                return;
              }

              // Scenario: Multiple matches
              if (totalCount > 1) {
                setSearchResults(typedResults);
                lastSearchedInsurerNumberRef.current = insurerNumber;
                setInsurerSearchTotalCount(totalCount);
                resolve(typedResults);
                return;
              }

              resolve(typedResults);
            },
            onError: (error: Error) => {
              setIsLoading(false);
              modal.error({
                title: 'エラー',
                content: '保険者情報の検索に失敗しました。',
                okText: 'OK',
                onOk: () => {
                  // Clear input when user closes the error dialog
                  onClearInputCallbackRef.current?.();
                },
              });
              options?.onError?.(error);
              resolve(null);
            },
          }
        );
      });
    },
    [
      clinic,
      searchMutate,
      getEmptyInsurerNumberMessage,
      getWarningDialog1Message,
      getWarningDialog2Message,
      options,
    ]
  );

  const showMultipleResultsDialog = (
    results: InsurerSearchResult[],
    insuranceTypeId: number,
    onSelect: (result: InsurerSearchResult) => void,
    onClose: () => void
  ) => {
    // Skip merger/reorganization warning: open selection modal directly for simple input UX
    // Original: For 一般 (insuranceTypeId === 1), show warning then open modal on OK
    // if (insuranceTypeId === 1) {
    //   modal.warning({
    //     title: '警告',
    //     content: getMultipleResultsWarningMessage(),
    //     okText: 'OK',
    //     onOk: () => {
    //       setCurrentInsuranceTypeId(insuranceTypeId);
    //       onSelectCallbackRef.current = onSelect;
    //       onCloseCallbackRef.current = onClose;
    //       setSearchResults(results);
    //       setIsModalOpen(true);
    //     },
    //   });
    // } else {
    //   setCurrentInsuranceTypeId(insuranceTypeId);
    //   onSelectCallbackRef.current = onSelect;
    //   onCloseCallbackRef.current = onClose;
    //   setSearchResults(results);
    //   setIsModalOpen(true);
    // }
    setCurrentInsuranceTypeId(insuranceTypeId);
    onSelectCallbackRef.current = onSelect;
    onCloseCallbackRef.current = onClose;
    setSearchResults(results);
    setIsModalOpen(true);
  };

  const handleModalSelect = (result: InsurerSearchResult) => {
    if (onSelectCallbackRef.current) {
      onSelectCallbackRef.current(result);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setInsurerSearchTotalCount(0);
    if (onCloseCallbackRef.current) {
      onCloseCallbackRef.current();
    }
  };

  const insurerSearchLoadPage = useCallback(
    async (pageIndex: number): Promise<InsurerSearchResult[]> => {
      const num = lastSearchedInsurerNumberRef.current;
      if (!num) return [];
      const items = await fetchPage(num, pageIndex);
      return items.map(mapInsurerToListResult);
    },
    [fetchPage]
  );

  const primeInsurerSearchPagingContext = useCallback(
    (insurerNumber: string, totalCount: number) => {
      const trimmed = (insurerNumber ?? '').toString().trim();
      if (!trimmed) return;
      lastSearchedInsurerNumberRef.current = trimmed;
      lastSearchTotalCountRef.current = totalCount;
      setInsurerSearchTotalCount(totalCount);
    },
    []
  );

  const getLastInsurerSearchTotalCount = useCallback(() => lastSearchTotalCountRef.current, []);

  return {
    isLoading,
    searchResults,
    handleSearch,
    showMultipleResultsDialog,
    isModalOpen,
    currentInsuranceTypeId,
    onSelectCallbackRef,
    onCloseCallbackRef,
    onClearInputCallbackRef,
    handleModalSelect,
    handleModalClose,
    insurerSearchTotalCount,
    insurerSearchLoadPage,
    primeInsurerSearchPagingContext,
    getLastInsurerSearchTotalCount,
  };
}
